import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, unref as _unref, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, vModelText as _vModelText, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "bg-white font-poppins" }
const _hoisted_2 = {
  role: "list",
  class: "border rounded-lg border-solid border-black text-black px-4 sm:px-6 py-2"
}
const _hoisted_3 = { class: "min-w-0 flex-1" }
const _hoisted_4 = { class: "text-md font-poppins-bold text-black my-0 py-0 flex items-center" }
const _hoisted_5 = {
  key: 0,
  class: "tooltip ml-1"
}
const _hoisted_6 = {
  key: 0,
  class: "text-sm mt-0 mb-0 py-0"
}
const _hoisted_7 = {
  key: 1,
  class: "inline-block text-xs font-bold mt-2 py-0.5 uppercase text-white bg-black px-4 rounded-full tracking-widest"
}
const _hoisted_8 = { class: "ml-6 flex flex-col items-end text-right" }
const _hoisted_9 = {
  key: 0,
  class: "my-0 py-0 font-poppins-bold text-md text-gray-900"
}
const _hoisted_10 = {
  key: 1,
  class: "text-sm my-0 py-0"
}
const _hoisted_11 = { class: "" }
const _hoisted_12 = { class: "flex flex-col items-start my-6 justify-between py-6 px-4 sm:px-6 border rounded-lg border-solid border-black text-black" }
const _hoisted_13 = { class: "flex flex-col w-full" }
const _hoisted_14 = { class: "flex flex-row pt-4 justify-between" }
const _hoisted_15 = {
  key: 0,
  class: "mt-1 font-normal text-sm text-green-500"
}
const _hoisted_16 = {
  key: 1,
  class: "mt-1 font-normal text-sm text-red-500"
}
const _hoisted_17 = {
  key: 0,
  class: "flex items-left justify-start py-4 px-4 sm:px-6 border rounded-lg border-solid border-black text-black flex-col"
}
const _hoisted_18 = {
  key: 0,
  class: "flex items-center justify-between border-t border-gray-200 py-1 px-4 sm:px-6"
}
const _hoisted_19 = {
  key: 1,
  class: "flex items-center justify-between border-t border-gray-200 py-1 px-4 sm:px-6"
}
const _hoisted_20 = {
  key: 2,
  class: "flex items-center justify-between border-t border-gray-200 py-1 px-4 sm:px-6"
}
const _hoisted_21 = {
  key: 3,
  class: "flex items-center justify-between border-t border-gray-200 py-1 px-4 sm:px-6"
}
const _hoisted_22 = { class: "flex flex-row items-start my-6 justify-between py-6 px-4 sm:px-6 border rounded-lg border-solid border-black text-black" }
const _hoisted_23 = { class: "text-md font-poppins-bold" }
const _hoisted_24 = { class: "text-md font-poppins pt-6 max-w-md" }
const _hoisted_25 = { class: "font-poppins-bold" }
const _hoisted_26 = { class: "text-md font-poppins-bold" }

import { computed, ref, watch } from 'vue';
import { injectStrict } from '@/plugins/vueServiceContainerPlugin';
import { Product } from '@/adapters/catalogAdapterInterface';
import { OrderServiceInterface } from '@/services/orderServiceInterface';
import { UtilityServiceInterface } from '@/services/utilityServiceInterface';
import { InformationCircleIcon } from '@heroicons/vue/outline';
import { isActionApplicableToPlan } from '@/utils/promotionActions';
import { Action, Promotion } from '@uniti-internet/promotions-js-client/dist/utilities/types';


export default /*@__PURE__*/_defineComponent({
  __name: 'OrderSummary',
  props: {
    products: {},
    isPromoValid: { type: Boolean },
    promotionDiscounts: {},
    validPromotion: {},
    suggestedPromotion: {},
    promoCode: {}
  },
  emits: ["onEnterPromoCode"],
  setup(__props: any, { emit: __emit }) {

const orderService = injectStrict<OrderServiceInterface>('OrderServiceInterface');
const utilityService = injectStrict<UtilityServiceInterface>('UtilityServiceInterface');

const props = __props;

const emit = __emit;

// Order the cart the way customers read it: plan, router, VoIP, then one-off fees.
const cartItemOrder = (product: Product): number => {
  if (product.type === 'internet') return 0;
  if (product.type === 'hardware') return 1;
  if (product.type === 'voice') return 2;
  if (product.code === 'ACTIVATION-FEE') return 3;
  if (product.code === 'NDC') return 4;
  return -1;
};

const cartItems = computed(() => {
  return props.products
    .filter(product => cartItemOrder(product) >= 0)
    .sort((a, b) => cartItemOrder(a) - cartItemOrder(b));
});

const isPlanHardwareOrVoice = (product: Product): boolean => {
  return product.type === 'internet' || product.type === 'hardware' || product.type === 'voice';
};

const itemLabel = (product: Product): string => {
  if (product.type === 'internet') return `Plan: ${product.name}`;
  if (product.type === 'hardware') return `Router: ${product.name}`;
  if (product.type === 'voice') return `VoIP: ${product.name}`;
  if (product.code === 'ACTIVATION-FEE') return 'Activation Fee';
  return 'New Development Fee';
};

// Plans, routers and VoIP strike through when discounted; fees strike through when the discount is displayed.
const isStruckThrough = (product: Product): boolean => {
  if (!props.isPromoValid) return false;
  return isPlanHardwareOrVoice(product) ? Boolean(product.discounted) : Boolean(product.displayDiscount);
};

const delayedNdc = computed(() => {
  return props.promotionDiscounts.find(discount => discount.actionCatalogObject.code === 'DELAYED-NDC');
});

const yearOff = computed(() => {
  return props.promotionDiscounts.find(discount => discount.actionCatalogObject.code === 'YEAR-OFF' && isActionApplicableToPlan(discount, props.products));
});

const monthOff = computed(() => {
  let plan = '';
  if (props.products.length > 0 && props.products.filter(pr => pr.type === 'internet').length > 0) {
    plan = props.products.filter(pr => pr.type === 'internet')[0].id;
  }
  return props.promotionDiscounts.find(discount => discount.actionCatalogObject.code === 'ONE-OFF' && (discount.applicableTo.length <= 0 || (discount.applicableTo.length > 0 && discount.applicableTo.includes(plan))));
});

const preInstalledRouter = computed(() => {
  return props.promotionDiscounts.find(discount => discount.actionCatalogObject.code === 'FREE-ROUTER-INSTALLED');
});

const promoCode = ref(props.promoCode ?? props.suggestedPromotion?.code);

// Keep the input in sync when the promo code is set by the parent (e.g. a suggested promotion).
watch(() => props.promoCode, (code) => {
  if (code !== undefined) {
    promoCode.value = code;
  }
});

function promoCodeClickHandler () {
  let promoCodeUppercase;
  if (promoCode.value) {
    promoCodeUppercase = promoCode.value.toUpperCase();
  }
  emit('onEnterPromoCode', promoCodeUppercase);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[12] || (_cache[12] = _createElementVNode("h3", { class: "sr-only" }, "Items in your cart", -1)),
    _createElementVNode("div", _hoisted_2, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(cartItems.value, (product) => {
        return (_openBlock(), _createElementBlock("div", {
          key: product.id,
          role: "listitem",
          class: "flex justify-between py-4"
        }, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("h4", _hoisted_4, [
              _createTextVNode(_toDisplayString(itemLabel(product)) + " ", 1),
              (product.code === 'NDC')
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                    (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(InformationCircleIcon)), {
                      class: "h-5 w-5 text-brand-button-text",
                      "aria-hidden": "true"
                    })),
                    _cache[1] || (_cache[1] = _createElementVNode("span", { class: "tooltiptext" }, [
                      _createElementVNode("p", { class: "tooltipmsg" }, "The New Development Charge or NDC is a charge that is passed on from the network wholesale provider to residents via the Retail Service Provider. This fee is applied to all new developments as a one-time fee for the first user to activate an internet service.")
                    ], -1))
                  ]))
                : _createCommentVNode("", true)
            ]),
            (isPlanHardwareOrVoice(product) && product.description)
              ? (_openBlock(), _createElementBlock("p", _hoisted_6, _toDisplayString(product.description), 1))
              : _createCommentVNode("", true),
            (product.type === 'internet')
              ? (_openBlock(), _createElementBlock("span", _hoisted_7, " Unlimited data "))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("p", {
              class: _normalizeClass(["my-0 py-0 font-poppins-bold text-md", [isStruckThrough(product) ? 'line-through text-gray-300' : 'text-gray-900']])
            }, _toDisplayString(_unref(utilityService).formatCurrency(product.price)), 3),
            (_ctx.isPromoValid && product.discounted)
              ? (_openBlock(), _createElementBlock("p", _hoisted_9, _toDisplayString(_unref(utilityService).formatCurrency(product.discountedPrice ?? 0)), 1))
              : _createCommentVNode("", true),
            (product.type === 'internet' || product.type === 'voice')
              ? (_openBlock(), _createElementBlock("p", _hoisted_10, "Monthly"))
              : _createCommentVNode("", true)
          ])
        ]))
      }), 128))
    ]),
    _createElementVNode("dl", _hoisted_11, [
      _createElementVNode("div", _hoisted_12, [
        _cache[3] || (_cache[3] = _createElementVNode("h2", { class: "text-lg font-poppins-bold my-0 py-0" }, "Promotions", -1)),
        _cache[4] || (_cache[4] = _createElementVNode("h3", { class: "sr-only" }, "Enter promotion code here", -1)),
        _createElementVNode("div", _hoisted_13, [
          _cache[2] || (_cache[2] = _createElementVNode("label", {
            for: "promoCode",
            class: "block text-md font-medium text-black pb-1"
          }, "Do you have a promotion code?", -1)),
          _createElementVNode("div", _hoisted_14, [
            _withDirectives(_createElementVNode("input", {
              type: "text",
              name: "promoCode",
              id: "promoCode",
              autocomplete: "off",
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((promoCode).value = $event)),
              class: "uppercase block w-full rounded-full shadow-sm sm:text-sm bg-gray-200 border-none max-w-md text-lg font-poppins font-bold px-6"
            }, null, 512), [
              [_vModelText, promoCode.value]
            ]),
            _createElementVNode("button", {
              onClick: promoCodeClickHandler,
              type: "button",
              class: "cursor-pointer items-center ml-2 px-4 py-2 border border-black border-solid shadow-sm text-sm md:text-lg font-poppins rounded-full text-black bg-white hover:bg-black hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-accent"
            }, "Apply Code")
          ]),
          (_ctx.isPromoValid)
            ? (_openBlock(), _createElementBlock("p", _hoisted_15, "Promo code " + _toDisplayString(_ctx.validPromotion?.code) + " applied!", 1))
            : _createCommentVNode("", true),
          (!_ctx.isPromoValid && promoCode.value && promoCode.value.length > 0)
            ? (_openBlock(), _createElementBlock("p", _hoisted_16, "Promo code invalid!"))
            : _createCommentVNode("", true)
        ])
      ]),
      (_ctx.isPromoValid && promoCode.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
            _cache[9] || (_cache[9] = _createElementVNode("h2", { class: "text-lg font-poppins-bold my-0 py-0" }, "Promotion includes:", -1)),
            (delayedNdc.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_18, _cache[5] || (_cache[5] = [
                  _createElementVNode("dt", { class: "text-sm font-extralight" }, " • The new development fee of $300 will be applied in month 13 ", -1)
                ])))
              : _createCommentVNode("", true),
            (yearOff.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_19, _cache[6] || (_cache[6] = [
                  _createElementVNode("dt", { class: "text-sm font-extralight" }, " • First Year Free! - Standard billing will commence in month 13 ", -1)
                ])))
              : _createCommentVNode("", true),
            (monthOff.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_20, _cache[7] || (_cache[7] = [
                  _createElementVNode("dt", { class: "text-sm font-extralight" }, " • First Month Free! ", -1)
                ])))
              : _createCommentVNode("", true),
            (preInstalledRouter.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_21, _cache[8] || (_cache[8] = [
                  _createElementVNode("dt", { class: "text-sm font-extralight" }, " • Pre-Installed router ", -1)
                ])))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_22, [
        _createElementVNode("div", _hoisted_23, [
          _cache[11] || (_cache[11] = _createTextVNode(" First Invoice Total ")),
          _createElementVNode("dt", _hoisted_24, [
            _cache[10] || (_cache[10] = _createTextVNode(" The total minimum cost of service over duration of the contract selected less any discounts is ")),
            _createElementVNode("span", _hoisted_25, _toDisplayString(_unref(utilityService).formatCurrency(_unref(orderService).calculateMinimumCost(_ctx.products, _ctx.isPromoValid, _ctx.validPromotion))), 1)
          ])
        ]),
        _createElementVNode("div", _hoisted_26, _toDisplayString(_unref(utilityService).formatCurrency(_unref(orderService).calculateFirstInvoiceTotal(_ctx.products, _ctx.isPromoValid, _ctx.validPromotion))), 1)
      ])
    ])
  ]))
}
}

})